import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, User, Phone, Mail, MapPin, Calendar, Building2, Users, AlertCircle,
  Loader2, Car, CreditCard, HandCoins, StickyNote, UserPlus, Send,
} from "lucide-react";
import { adminGet, adminPost, money, titleCase } from "./utils";

function StatCard({ label, value, tone = "default", testid }) {
  const toneCls = tone === "warn"
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : tone === "danger"
      ? "border-red-300 bg-red-50 text-red-900"
      : "border-brand-navy/10 bg-white text-brand-deep";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-70">{label}</div>
      <div className="font-serif text-xl mt-1">{value}</div>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-brand-muted shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">{label}</div>
        <div className="text-sm text-brand-deep font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

export function PatientProfileDrawer({ patientId, auth, onClose, onOpenRide }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteBusy, setInviteBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const d = await adminGet(auth, `patients/${encodeURIComponent(patientId)}`);
        if (mounted) setData(d);
      } catch (err) {
        toast.error(err.message || "Failed to load patient profile");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [auth, patientId]);

  const sendInvite = async () => {
    const email = data?.patient?.email;
    if (!email) return toast.error("No email on record — ask the customer for one first.");
    const note = window.prompt("Optional note (shown in the invite email — leave blank to skip):", "");
    if (note === null) return; // user cancelled
    setInviteBusy(true);
    try {
      const res = await adminPost(auth, `patients/${encodeURIComponent(patientId)}/send-account-invite`, {
        origin_url: window.location.origin,
        note: note?.trim() || undefined,
      });
      if (res.status === "already_registered") {
        toast.info(`${res.patient_name || email} already has an account`);
      } else {
        toast.success(`Invite emailed to ${res.email}${res.ride_count ? ` · ${res.ride_count} ride${res.ride_count === 1 ? "" : "s"} will merge on signup` : ""}`);
      }
    } catch (err) {
      toast.error(err.message || "Could not send invite");
    } finally {
      setInviteBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end" data-testid="patient-profile-drawer">
      <div className="bg-white w-full sm:w-[560px] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-brand-navy/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Patient profile</div>
            <h2 className="font-serif text-2xl text-brand-deep leading-tight">{data?.patient?.name || "…"}</h2>
          </div>
          <button onClick={onClose} data-testid="close-patient-profile" className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-brand-muted"><Loader2 className="inline w-6 h-6 animate-spin" /></div>
        ) : !data ? null : (
          <div className="p-6 space-y-6">
            {/* Balance / trip stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard label="Trips" value={data.rides.length} testid="stat-total-trips" />
              <StatCard label="Completed" value={data.totals.trips_completed} testid="stat-completed" />
              <StatCard label="Lifetime paid" value={money(data.totals.lifetime_captured_cents)} testid="stat-lifetime" />
              <StatCard
                label="Balance owed"
                value={money(data.totals.outstanding_balance_cents)}
                tone={data.totals.outstanding_balance_cents > 0 ? "warn" : "default"}
                testid="stat-balance"
              />
            </div>

            {data.totals.action_needed_count > 0 && (
              <div className="rounded-xl border border-red-300 bg-red-50 p-3 flex items-start gap-2.5" data-testid="profile-action-needed">
                <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="text-xs text-red-900">
                  <div className="font-bold uppercase tracking-[0.14em] text-[10px] mb-0.5">{data.totals.action_needed_count} ride(s) need attention</div>
                  This customer is blocked from new bookings until every unresolved payment is cleared.
                </div>
              </div>
            )}

            {/* Guest bookee — offer account invite */}
            {data.source === "guest_ride" && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="guest-invite-banner">
                <div className="flex items-start gap-3">
                  <UserPlus className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold uppercase tracking-[0.14em] text-[10px] text-amber-900 mb-1">Guest bookee — no portal account</div>
                    <div className="text-xs text-amber-900 mb-3">
                      This customer booked without creating an account. Send them a signup invite — their {data.rides.length} historical ride{data.rides.length === 1 ? "" : "s"} will auto-link so future bookings save a card on file.
                    </div>
                    <button
                      onClick={sendInvite}
                      disabled={!data.patient.email || inviteBusy}
                      data-testid="send-account-invite"
                      className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-40"
                      title={!data.patient.email ? "No email on record — ask the customer for an email first" : ""}
                    >
                      {inviteBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send account invite
                    </button>
                    {!data.patient.email && (
                      <div className="text-[11px] text-amber-800 mt-1.5">No email on record — ask the customer for one first.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contact + identity */}
            <section>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">Contact & identity</div>
              <div className="bg-brand-surfaceAlt rounded-xl p-4 divide-y divide-brand-navy/5">
                <Field icon={User} label="Name" value={data.patient.name} testid="field-name" />
                <Field icon={Phone} label="Phone" value={data.patient.phone} testid="field-phone" />
                <Field icon={Mail} label="Email" value={data.patient.email} testid="field-email" />
                <Field icon={Calendar} label="DOB" value={data.patient.dob} testid="field-dob" />
                <Field icon={MapPin} label="Home address" value={data.patient.home_address} testid="field-address" />
                <Field icon={CreditCard} label="Medicaid ID" value={data.patient.medicaid_id} testid="field-medicaid" />
                <Field icon={CreditCard} label="Insurance plan" value={data.patient.insurance_plan} testid="field-insurance" />
                <Field icon={User} label="Doctor" value={data.patient.doctor_name && `${data.patient.doctor_name}${data.patient.doctor_phone ? " · " + data.patient.doctor_phone : ""}`} testid="field-doctor" />
                <Field icon={Calendar} label="Signed up" value={data.patient.created_at?.slice(0, 10)} testid="field-signup" />
              </div>
            </section>

            {/* Group / facility */}
            {data.group && (
              <section data-testid="section-group">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">Facility / group</div>
                <div className="bg-brand-surfaceAlt rounded-xl p-4 divide-y divide-brand-navy/5">
                  <Field icon={Building2} label="Facility" value={data.group.name} />
                  <Field icon={Phone} label="Phone" value={data.group.phone} />
                  <Field icon={Mail} label="Email" value={data.group.email} />
                  <Field icon={MapPin} label="Address" value={data.group.address} />
                </div>
              </section>
            )}

            {/* Saved payment methods */}
            {data.patient.saved_payment_methods?.length > 0 && (
              <section data-testid="section-payment-methods">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">Saved payment methods</div>
                <div className="space-y-2">
                  {data.patient.saved_payment_methods.map((pm, i) => (
                    <div key={i} className="bg-white border border-brand-navy/10 rounded-xl p-3 flex items-center gap-3" data-testid={`payment-method-${i}`}>
                      <div className="text-2xl">{
                        pm.type === "card" ? "💳" :
                        pm.type === "zelle" ? "📱" :
                        pm.type === "check" ? "🧾" : "💵"
                      }</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-brand-deep">
                          {pm.type === "card"
                            ? `${(pm.brand || "").toUpperCase()} ····${pm.last4 || "····"}`
                            : `${pm.type.toUpperCase()} · ${pm.handle || "no ref"}`}
                        </div>
                        <div className="text-[11px] text-brand-muted">
                          {pm.source === "driver_collection" ? "Recorded by driver" : "Saved at checkout"} · {pm.added_at?.slice(0, 10)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Family members */}
            {data.patient.family_members?.length > 0 && (
              <section data-testid="section-family">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">Family / authorized contacts</div>
                <div className="space-y-2">
                  {data.patient.family_members.map((fm, i) => (
                    <div key={i} className="bg-white border border-brand-navy/10 rounded-xl p-3" data-testid={`family-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-brand-green" />
                        <div className="font-semibold text-brand-deep">{fm.name || "—"}</div>
                        {fm.relationship && <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">· {fm.relationship}</div>}
                      </div>
                      {fm.phone && <div className="text-xs text-brand-muted">📞 {fm.phone}</div>}
                      {fm.email && <div className="text-xs text-brand-muted">✉️ {fm.email}</div>}
                      {fm.notify_email !== undefined && (
                        <div className="text-[11px] text-brand-muted mt-1">
                          Notifications: {fm.notify_email ? "email ✓" : "off"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Notes */}
            {data.patient.notes && (
              <section data-testid="section-notes">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">Notes</div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2">
                  <StickyNote className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{data.patient.notes}</div>
                </div>
              </section>
            )}

            {/* Rides */}
            <section data-testid="section-rides">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">
                Ride history ({data.rides.length})
              </div>
              {data.rides.length === 0 ? (
                <div className="text-sm text-brand-muted italic">No rides yet.</div>
              ) : (
                <div className="space-y-2">
                  {data.rides.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => onOpenRide && onOpenRide(r)}
                      data-testid={`profile-ride-${r.id}`}
                      className={`w-full text-left bg-white border rounded-xl p-3 hover:border-brand-green transition ${r.action_needed ? "border-red-300" : "border-brand-navy/10"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-brand-deep">
                          {r.trip_date} · {r.trip_time}
                        </div>
                        <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded-full ${
                          r.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                          r.status === "cancelled" ? "bg-red-100 text-red-800" :
                          r.action_needed ? "bg-red-100 text-red-800" :
                          "bg-amber-100 text-amber-800"
                        }`}>
                          {r.action_needed ? "Action needed" : titleCase(r.status || "")}
                        </div>
                      </div>
                      <div className="text-xs text-brand-muted mt-1">
                        {r.pickup} → {r.dropoff}
                      </div>
                      <div className="flex items-center justify-between mt-2 text-[11px] text-brand-muted">
                        <div className="flex items-center gap-2">
                          {r.driver_name && <><Car className="w-3 h-3" /> {r.driver_name}</>}
                          {r.miles && <span>· {r.miles.toFixed(1)} mi</span>}
                        </div>
                        <div>
                          {r.captured_amount_cents ? <>Paid <strong className="text-brand-deep">{money(r.captured_amount_cents)}</strong></> :
                            r.hold_amount_cents ? <>Hold {money(r.hold_amount_cents)}</> : null}
                          {r.balance_owed_cents ? <span className="text-red-700 font-semibold"> · Balance {money(r.balance_owed_cents)}</span> : null}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
